import clsx from 'clsx'
import type { ButtonHTMLAttributes, ReactNode } from 'react'
import '../../styles/slp.css'

/**
 * SLP Professional primitives — React wrappers over the pure-CSS classes in
 * styles/slp.css (redesign V2, approved 2026-06-21).
 *
 * These deliberately mirror apps/session/src/components/slp/* in naming and
 * data-testids so session can migrate onto them later with zero test churn.
 * No Tailwind: consumers only need the CSS file (auto-imported here).
 */

// ── Panel ──────────────────────────────────────────────────────────────────
export function SlpPanel({
  title,
  children,
  className,
  bodyClassName,
}: {
  title: string
  children: ReactNode
  className?: string
  /** e.g. pass 'slp-panel__body--flush' style overrides or set padding:0 */
  bodyClassName?: string
}) {
  return (
    <section
      data-testid={`slp-panel-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
      className={clsx('slp-panel', className)}
    >
      <header className="slp-panel__header">{title}</header>
      <div className={clsx('slp-panel__body', bodyClassName)}>{children}</div>
    </section>
  )
}

// ── Button ─────────────────────────────────────────────────────────────────
export function SlpButton({
  variant = 'primary',
  className,
  children,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary' | 'danger'
}) {
  return (
    <button
      type="button"
      {...rest}
      className={clsx(
        'slp-btn',
        variant === 'secondary' && 'slp-btn--secondary',
        variant === 'danger' && 'slp-btn--danger',
        className,
      )}
    >
      {children}
    </button>
  )
}

// ── Status lamp ────────────────────────────────────────────────────────────
export type SlpLampStatus = 'ok' | 'warn' | 'error' | 'off'

const LAMP_LABEL: Record<SlpLampStatus, string> = {
  ok: 'online',
  warn: 'warning',
  error: 'error',
  off: 'offline',
}

export function SlpStatusLamp({
  status,
  label,
  size = 'md',
  className,
}: {
  status: SlpLampStatus
  label?: string
  size?: 'sm' | 'md' | 'lg'
  className?: string
}) {
  return (
    <span
      role="status"
      aria-label={label ?? LAMP_LABEL[status]}
      data-status={status}
      className={clsx(
        'slp-lamp',
        status === 'ok' && 'slp-lamp--ok',
        status === 'warn' && 'slp-lamp--warn',
        status === 'error' && 'slp-lamp--error',
        size === 'sm' && 'slp-lamp--sm',
        size === 'lg' && 'slp-lamp--lg',
        className,
      )}
    />
  )
}

// ── Bottom status bar ──────────────────────────────────────────────────────
export function SlpStatusBar({
  left,
  right,
  className,
}: {
  left: ReactNode
  right?: ReactNode
  className?: string
}) {
  return (
    <div data-testid="slp-statusbar" className={clsx('slp-statusbar', className)}>
      <span>{left}</span>
      {right != null ? <span>{right}</span> : null}
    </div>
  )
}

// ── Segmented meter ────────────────────────────────────────────────────────
/**
 * Horizontal segmented level meter on a dark track. `db` in dBFS (−60…0);
 * segments light green→yellow→red like the session Audio Levels panel.
 * Pure visual (no audio wiring) — feed it a level from wherever.
 */
export function SlpMeter({
  db,
  segments = 16,
  className,
}: {
  db: number
  segments?: number
  className?: string
}) {
  const clamped = Math.max(-60, Math.min(0, db))
  const lit = Math.round(((clamped + 60) / 60) * segments)
  return (
    <div
      className={clsx('slp-meter', className)}
      role="meter"
      aria-valuemin={-60}
      aria-valuemax={0}
      aria-valuenow={Math.round(clamped)}
      aria-label={`level ${Math.round(clamped)} dBFS`}
    >
      <div className="slp-meter__fill">
        {Array.from({ length: segments }, (_, i) => {
          const frac = (i + 1) / segments // 0..1 across the scale
          const cls =
            i >= lit
              ? 'slp-meter__seg--off'
              : frac > 0.92
                ? 'slp-meter__seg--r'
                : frac > 0.75
                  ? 'slp-meter__seg--y'
                  : undefined
          // biome-ignore lint/suspicious/noArrayIndexKey: fixed-length visual segments
          return <span key={i} className={clsx('slp-meter__seg', cls)} />
        })}
      </div>
    </div>
  )
}
